import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { IllusDressing_1, theme } from '@sg/garnish';
import { OrderType } from '@sg/graphql-schema';

import { LoadingAnimation } from '../../components';
import { DigitalVoiceSurveyContent } from './RateOrderScreen.digitalVoice';
import { LandingContent } from './RateOrderScreen.landing';
import { MomosSurveyContent } from './RateOrderScreen.momos';

// ─────────────────────────────────────────────────────────────────────────────

export const RateOrderScreenReady = (props: RateOrderScreenReadyProps) => {
  const {
    isFetching,
    isInvalidOrder,
    isPendingOrder,
    isRatedOrder,
    momoSurveyUrl,
    orderId,
    orderType,
  } = props;

  const { formatMessage } = useIntl();

  if (isFetching) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingAnimation />
      </View>
    );
  }

  if (isInvalidOrder) {
    return (
      <LandingContent>
        <LandingContent.Illustration
          source={IllusDressing_1}
          style={styles.notFoundViewIllustration}
        />

        <LandingContent.Title>
          {formatMessage(messages.rateOrderNotFoundOrderViewTitle)}
        </LandingContent.Title>

        <LandingContent.Subtitle>
          {formatMessage(messages.rateOrderNotFoundOrderViewSubtitle)}
        </LandingContent.Subtitle>
      </LandingContent>
    );
  }

  if (isPendingOrder) {
    return (
      <LandingContent>
        <LandingContent.Illustration />

        <LandingContent.Title>
          {formatMessage(messages.rateOrderPendingOrderViewTitle)}
        </LandingContent.Title>

        <LandingContent.Subtitle>
          {formatMessage(messages.rateOrderPendingOrderViewSubtitle)}
        </LandingContent.Subtitle>
      </LandingContent>
    );
  }

  if (isRatedOrder) {
    return (
      <LandingContent>
        <LandingContent.Illustration />

        <LandingContent.Title>
          {formatMessage(messages.rateOrderRatedOrderViewTitle)}
        </LandingContent.Title>

        <LandingContent.Subtitle>
          {formatMessage(messages.rateOrderRatedOrderViewSubtitle)}
        </LandingContent.Subtitle>
      </LandingContent>
    );
  }

  // ─── Momos Survey ──────────────────────────────────────────────────────────

  if (momoSurveyUrl && orderType) {
    return <MomosSurveyContent url={momoSurveyUrl} orderId={orderId} orderType={orderType} />;
  }

  // ─── Digital Voice Survey ──────────────────────────────────────────────────

  return <DigitalVoiceSurveyContent />;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  rateOrderClose: {
    defaultMessage: 'Close',
    description: 'Rate Order | Close',
  },
  rateOrderNotFoundOrderViewTitle: {
    defaultMessage: `We couldn't find this order.`,
    description: 'Rate | Order | Not Found Order View | Title',
  },
  rateOrderNotFoundOrderViewSubtitle: {
    defaultMessage: 'Please select another order to rate.',
    description: 'Rate | Order | Not Found Order View | Subtitle',
  },
  rateOrderPendingOrderViewTitle: {
    defaultMessage: 'This order is still in progress.',
    description: 'Rate | Order | Pending Order View | Title',
  },
  rateOrderPendingOrderViewSubtitle: {
    defaultMessage: 'Come back to rate this order once it has been completed.',
    description: 'Rate | Order | Pending Order View | Subtitle',
  },
  rateOrderRatedOrderViewTitle: {
    defaultMessage: 'You’ve already rated this order.',
    description: 'Rate | Order | Rated Order View | Title',
  },
  rateOrderRatedOrderViewSubtitle: {
    defaultMessage: 'Thank you! Your feedback means a lot.',
    description: 'Rate | Order | Rated Order View | Subtitle',
  },
  rateOrderMomosSurveyModal: {
    defaultMessage: 'Rate order survey',
    description: 'Rate | Order | Momos Survey | Modal Accessibility Label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    paddingTop: theme.spacing['10'],
    paddingBottom: theme.spacing['20'],
  },
  notFoundViewIllustration: {
    width: 200,
    height: 200,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type RateOrderScreenReadyProps = Readonly<{
  isFetching: boolean;
  isInvalidOrder: boolean;
  isPendingOrder: boolean;
  isRatedOrder: boolean;
  momoSurveyUrl: string | null;
  orderId: string;
  orderType: OrderType | null;
}>;
